import { useState, useEffect, useMemo } from "react";
import DeckGL from "@deck.gl/react";
import Map from "react-map-gl/maplibre";
import { PathLayer, ScatterplotLayer } from "@deck.gl/layers";
import "maplibre-gl/dist/maplibre-gl.css";

const MAP_STYLE = "https://api.maptiler.com/maps/base-v4/style.json?key=qjoXX9L2A2sutJRBuk7t";

const FOCUS_PORTS = ["kingston", "bridgetown"];

const INITIAL_VIEW = {
  longitude: -30,
  latitude: 10,
  zoom: 2.5,
  minZoom: 1.5,
  maxZoom: 15,
  pitch: 0,
  bearing: 0,
};

function cubicBezier(p0, c1, c2, p3, segments = 32) {
  const pts = [];
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const mt = 1 - t;
    pts.push([
      mt*mt*mt*p0[0] + 3*mt*mt*t*c1[0] + 3*mt*t*t*c2[0] + t*t*t*p3[0],
      mt*mt*mt*p0[1] + 3*mt*mt*t*c1[1] + 3*mt*t*t*c2[1] + t*t*t*p3[1],
    ]);
  }
  return pts;
}

function buildEdgePath(rawPath) {
  if (!rawPath || rawPath.length < 2) return [];
  if (rawPath.length === 2) {
    const [p0, p3] = rawPath;
    const midLon = (p0[0] + p3[0]) / 2;
    const midLat = (p0[1] + p3[1]) / 2 + 4;
    return cubicBezier(p0, [midLon, midLat], [midLon, midLat], p3, 32);
  }
  const [p0, c1, c2, p3] = rawPath;
  if (!p0 || !c1 || !c2 || !p3) return [];
  return cubicBezier(p0, c1, c2, p3, 32);
}

export default function Home() {
  const [transatlantic, setTransatlantic]     = useState([]);
  const [intraamerican, setIntraamerican]     = useState([]);
  const [ports, setPorts]                     = useState();
  const [origin, setOrigin]                   = useState();
  const [focusCache, setFocusCache]           = useState({});
  const [activePort, setActivePort]           = useState(null);
  const [focusMode, setFocusMode]             = useState(null);
  const [showTransatlantic, setShowTransatlantic] = useState(true);
  const [showIntraamerican, setShowIntraamerican] = useState(true);
  const [selectedPort, setSelectedPort]       = useState(null);
  const [kingstonVoyages, setKingstonVoyages] = useState([]);
  const [kingstonStats, setKingstonStats]     = useState(null);
  const [showKingstonModal, setShowKingstonModal] = useState(false);
  const [kingstonLoading, setKingstonLoading] = useState(false);

  function openKingstonModal() {
    setShowKingstonModal(true);
    if (kingstonVoyages.length) return;
    setKingstonLoading(true);
    fetch("/api/get/kingston-voyages")
      .then(r => r.json())
      .then(j => {
        if (j.status === "success") {
          setKingstonVoyages(j.voyages);
          setKingstonStats(j.stats);
        }
      })
      .catch(err => console.error("Kingston voyages error:", err))
      .finally(() => setKingstonLoading(false));
  }

  useEffect(() => {
    fetch("/api/get/start")
      .then(res => res.json())
      .then(json => { if (json.status === "success") setOrigin(json.data); })
      .catch(err => console.error("Fetch error:", err));

    fetch("/api/get/ports")
      .then(res => res.json())
      .then(json => { if (json.status === "success") setPorts(json); })
      .catch(err => console.error("Fetch error:", err));

    fetch("/api/get/data")
      .then(res => res.json())
      .then(json => {
        if (json.status === "success") {
          setTransatlantic(json.transatlantic);
          setIntraamerican(json.intraamerican);
        }
      })
      .catch(err => console.error("Fetch error:", err));

    FOCUS_PORTS.forEach(name => {
      fetch(`/api/get/focus/${name}`)
        .then(res => res.json())
        .then(json => {
          if (json.status === "success")
            setFocusCache(prev => ({ ...prev, [name]: json }));
        })
        .catch(err => console.error(`Focus fetch error (${name}):`, err));
    });
  }, []);

  function handlePortButton(port, mode) {
    if (activePort === port && focusMode === mode) {
      setActivePort(null);
      setFocusMode(null);
    } else {
      setActivePort(port);
      setFocusMode(mode);
    }
  }

  function handleMapClick(info) {
    if (!info.object) {
      setSelectedPort(null);
      return;
    }
    const name = info.object.name?.toLowerCase() ?? "";
    if (name === "kingston") {
      openKingstonModal();
    } else {
      setSelectedPort(info.object);
    }
  }

  const focusData = activePort ? focusCache[activePort] : null;

  const networkLayers = useMemo(() => {
    const layers = [];
    if (showTransatlantic && transatlantic.length) {
      layers.push(new PathLayer({
        id: "transatlantic",
        data: transatlantic,
        getPath: d => buildEdgePath(d.path),
        getColor: [180, 120, 60, 200],
        getWidth: d => 1 + (d.weight / 10000000) * 10,
        widthUnits: "pixels",
        capRounded: false,
        jointRounded: true,
        pickable: false,
      }));
    }
    if (showIntraamerican && intraamerican.length) {
      layers.push(new PathLayer({
        id: "intraamerican",
        data: intraamerican,
        getPath: d => buildEdgePath(d.path),
        getColor: [80, 160, 200, 200],
        getWidth: d => 1 + (d.weight / 10000000) * 10,
        widthUnits: "pixels",
        capRounded: false,
        jointRounded: true,
        pickable: false,
      }));
    }
    return layers;
  }, [transatlantic, intraamerican, showTransatlantic, showIntraamerican]);

  const focusLayer = useMemo(() => {
    if (!focusData || !focusMode) return [];
    const data = focusMode === "incoming" ? focusData.incoming : focusData.outgoing;
    const color = focusMode === "incoming" ? [220, 100, 40, 220] : [14, 51, 134, 220];
    const maxCount = Math.max(...data.map(d => d.voyage_count));
    return [new PathLayer({
      id: "focus",
      data,
      getPath: d => d.segments.flatMap(seg => buildEdgePath(seg)),
      getColor: color,
      getWidth: d => 0.5 + (d.voyage_count / maxCount) * 4,
      widthUnits: "pixels",
      capRounded: false,
      jointRounded: true,
      pickable: false,
    })];
  }, [focusData, focusMode]);

  const portLayers = useMemo(() => {
    const purchaseData  = ports?.purchase.filter(d => Number(d.count) > 500) || [];
    const disembarkData = ports?.disembark.filter(d => Number(d.count) > 500) || [];
    const largeOrigin   = origin?.filter(d => Number(d.count) > 200) || [];
    return [
      new ScatterplotLayer({
        id: "ports-origin",
        data: largeOrigin,
        getPosition: d => [
          Number(d.begin_lon) < -5 && Number(d.begin_lat) < 30
            ? Number(d.begin_lon) + 0.2
            : Number(d.begin_lon),
          Number(d.begin_lat)
        ],
        getFillColor: [60, 179, 113, 200],
        radiusUnits: "pixels",
        getRadius: d => Math.sqrt(d.count || 1) * 0.4,
        radiusMinPixels: 3,
        pickable: true,
      }),
      new ScatterplotLayer({
        id: "ports-purchase",
        data: purchaseData,
        getPosition: d => [Number(d.lng), Number(d.lat)],
        getFillColor: [255, 140, 0, 200],
        radiusUnits: "pixels",
        getRadius: d => Math.sqrt(d.count || 1) * 0.4,
        radiusMinPixels: 3,
        pickable: true,
      }),
      new ScatterplotLayer({
        id: "ports-disembark",
        data: disembarkData,
        getPosition: d => [Number(d.lng), Number(d.lat)],
        getFillColor: [80, 160, 200, 200],
        radiusUnits: "pixels",
        getRadius: d => Math.sqrt(d.count || 1) * 0.4,
        radiusMinPixels: 4,
        pickable: true,
        updateTriggers: { getFillColor: [], getRadius: [] },
      }),
    ];
  }, [ports, origin]);

  const layers = [...networkLayers, ...focusLayer, ...portLayers];

  const btnBase = {
    border: "none", borderRadius: 4, color: "white",
    padding: "6px 12px", cursor: "pointer", textAlign: "left", fontSize: 12,
  };

  function portSection(portKey, label) {
    const isActive = activePort === portKey;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <span style={{ fontWeight: 600, fontSize: 13 }}>{label}</span>
        <button
          onClick={() => handlePortButton(portKey, "incoming")}
          style={{
            ...btnBase,
            background: isActive && focusMode === "incoming"
              ? "rgba(220,100,40,0.85)" : "rgba(255,255,255,0.12)",
          }}
        >
          Transatlantic routes
        </button>
        <button
          onClick={() => handlePortButton(portKey, "outgoing")}
          style={{
            ...btnBase,
            background: isActive && focusMode === "outgoing"
              ? "rgba(14,51,134,0.8)" : "rgba(255,255,255,0.12)",
          }}
        >
          Intraamerican routes
        </button>
      </div>
    );
  }

  // Stat bar sub-component
  function StatBar({ label, value, displayValue, color, highlight }) {
    return (
      <div style={{ marginTop: 8 }}>
        <div style={{
          display: "flex", justifyContent: "space-between",
          fontSize: 11, color: "rgba(255,255,255,0.55)", marginBottom: 4,
        }}>
          <span>{label}</span>
          <strong style={{ color: highlight ? "rgba(220,80,80,0.9)" : "white" }}>
            {displayValue}
          </strong>
        </div>
        <div style={{ height: 5, borderRadius: 3, background: "rgba(255,255,255,0.08)" }}>
          <div style={{
            height: "100%", borderRadius: 3,
            width: `${Math.min(value, 100)}%`,
            background: highlight ? "rgba(220,80,80,0.8)" : color,
            transition: "width 0.8s ease",
          }} />
        </div>
      </div>
    );
  }

  return (
    <div className="map">

      {/* Sidebar */}
      <div style={{
        position: "absolute", top: 20, left: 20, zIndex: 10,
        display: "flex", flexDirection: "column", gap: 16,
        background: "rgba(0,0,0,0.65)", padding: "14px 16px",
        borderRadius: 8, color: "white", minWidth: 200,
      }}>
        {/* Network toggles */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={{ fontWeight: 600, fontSize: 13 }}>Networks</span>
          <button
            onClick={() => setShowTransatlantic(s => !s)}
            style={{
              ...btnBase, textAlign: "center",
              background: showTransatlantic ? "rgba(180,120,60,0.55)" : "rgba(255,255,255,0.10)",
              border: `1px solid rgba(180,120,60,${showTransatlantic ? 0.9 : 0.3})`,
            }}
          >
            {showTransatlantic ? "Hide" : "Show"} Transatlantic
          </button>
          <button
            onClick={() => setShowIntraamerican(s => !s)}
            style={{
              ...btnBase, textAlign: "center",
              background: showIntraamerican ? "rgba(80,160,200,0.55)" : "rgba(255,255,255,0.10)",
              border: `1px solid rgba(80,160,200,${showIntraamerican ? 0.9 : 0.3})`,
            }}
          >
            {showIntraamerican ? "Hide" : "Show"} Intra-American
          </button>
        </div>

        <div style={{ borderTop: "1px solid rgba(255,255,255,0.15)" }} />

        {/* Kingston */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={{ fontWeight: 600, fontSize: 13 }}>Kingston, Jamaica</span>
          <button
            onClick={() => handlePortButton("kingston", "incoming")}
            style={{
              ...btnBase,
              background: activePort === "kingston" && focusMode === "incoming"
                ? "rgba(220,100,40,0.85)" : "rgba(255,255,255,0.12)",
            }}
          >
            Transatlantic routes
          </button>
          <button
            onClick={() => handlePortButton("kingston", "outgoing")}
            style={{
              ...btnBase,
              background: activePort === "kingston" && focusMode === "outgoing"
                ? "rgba(14,51,134,0.8)" : "rgba(255,255,255,0.12)",
            }}
          >
            Intraamerican routes
          </button>
        </div>

        <div style={{ borderTop: "1px solid rgba(255,255,255,0.15)" }} />
        {portSection("bridgetown", "Bridgetown, Barbados")}

        {(activePort || focusMode) && (
          <button
            onClick={() => { setActivePort(null); setFocusMode(null); }}
            style={{
              ...btnBase, background: "transparent",
              border: "1px solid rgba(255,255,255,0.25)",
              color: "rgba(255,255,255,0.55)", fontSize: 11,
            }}
          >
            Clear
          </button>
        )}
      </div>

      {/* Generic port tooltip */}
      {selectedPort && (
        <div style={{
          position: "absolute", bottom: 20, left: 20, zIndex: 20,
          background: "rgba(0,0,0,0.85)", padding: "12px", borderRadius: 8,
          color: "white", fontSize: 12, minWidth: 150,
          border: "1px solid rgba(255,255,255,0.2)",
        }}>
          <div style={{ fontWeight: "bold", marginBottom: 4, display: "flex", justifyContent: "space-between" }}>
            <span>{selectedPort.name || "Port Details"}</span>
            <span style={{ cursor: "pointer" }} onClick={() => setSelectedPort(null)}>✕</span>
          </div>
          <div>Voyages: {selectedPort.count}</div>
        </div>
      )}

      {showKingstonModal && (
        <div
          style={{
            position: "absolute", inset: 0, zIndex: 30,
            background: "rgba(0,0,0,0.6)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
          onClick={() => setShowKingstonModal(false)}
        >
          <div
            style={{
              background: "rgba(0, 0, 0, 0.65)", borderRadius: 10, padding: 24,
              scrollbarWidth: "none",
              maxWidth: 580, width: "90%", maxHeight: "80vh", overflowY: "auto",
              border: "1px solid rgba(255,255,255,0.15)", color: "white",
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>Kingston, Jamaica</div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", marginTop: 2 }}>
                  Notable voyages from the historical record
                </div>
              </div>
              <span
                style={{ cursor: "pointer", fontSize: 18, color: "rgba(255,255,255,0.5)" }}
                onClick={() => setShowKingstonModal(false)}
              >✕</span>
            </div>

            {kingstonLoading
              ? (
                <div style={{ textAlign: "center", padding: 32, color: "rgba(255,255,255,0.4)" }}>
                  Loading...
                </div>
              )
              : (
                <>
                  {/* Voyage cards grouped by direction */}
                  {["arriving", "departing"].map(direction => {
                    const group = kingstonVoyages.filter(v => v.direction === direction);
                    if (!group.length) return null;
                    return (
                      <div key={direction}>
                        <div style={{
                          fontSize: 11, fontWeight: 700, letterSpacing: 1.5,
                          textTransform: "uppercase", color: "rgba(255,255,255,0.35)",
                          marginBottom: 10,
                          marginTop: direction === "departing" ? 20 : 0,
                          paddingBottom: 6,
                          borderBottom: "1px solid rgba(255,255,255,0.08)",
                        }}>
                          {direction === "arriving" ? "↓ Arriving in Kingston" : "↑ Departing from Kingston"}
                        </div>
                        {group.map(v => (
                          <div key={`${v.voyage_id}-${direction}`} style={{
                            marginBottom: 12, padding: 14, borderRadius: 8,
                            background: "rgba(255,255,255,0.05)",
                            borderLeft: `3px solid ${
                              v.category === "Largest cargo"
                                ? "rgba(255,140,0,0.8)"
                                : "rgba(220,60,60,0.8)"
                            }`,
                          }}>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                              <span style={{
                                fontSize: 10, fontWeight: 600, letterSpacing: 1,
                                textTransform: "uppercase", color: "rgba(255,255,255,0.45)",
                              }}>{v.category}</span>
                              <span style={{ fontSize: 11, color: "rgba(255,255,255,0.4)" }}>
                                Voyage #{v.voyage_id}
                              </span>
                            </div>
                            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                              {direction === "arriving"
                                ? `${v.origin ?? "—"} → ${v.purchase ?? "—"} → Kingston`
                                : `Kingston → ${v.destination ?? "—"}`
                              }
                            </div>
                            <div style={{
                              display: "grid", gridTemplateColumns: "1fr 1fr",
                              gap: "4px 16px", fontSize: 12, color: "rgba(255,255,255,0.7)",
                            }}>
                              <div>Year: <strong>{v.year ?? "—"}</strong></div>
                              <div>Flag: <strong>{v.flag ?? "—"}</strong></div>
                              <div>Embarked: <strong>{v.embarked?.toLocaleString() ?? "—"}</strong></div>
                              <div>Disembarked: <strong>{v.disembarked?.toLocaleString() ?? "—"}</strong></div>
                              <div>Mortality: <strong style={{ color: v.mortality > 20 ? "rgba(220,80,80,0.9)" : "inherit" }}>
                                {v.mortality != null ? `${v.mortality}%` : "—"}
                              </strong></div>
                              <div>Resistance: <strong>{v.resistance ?? "None recorded"}</strong></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })}

                  {/* Historical averages */}
                  {kingstonStats && (
                    <div style={{ marginTop: 24 }}>
                      <div style={{
                        fontSize: 11, fontWeight: 700, letterSpacing: 1.5,
                        textTransform: "uppercase", color: "rgba(255,255,255,0.35)",
                        paddingBottom: 6, borderBottom: "1px solid rgba(255,255,255,0.08)",
                        marginBottom: 14,
                      }}>
                        Historical averages — all recorded voyages
                      </div>

                      {[
                        {
                          label:  "Transatlantic → Kingston",
                          key:    "arriving",
                          accent: "rgba(220,100,40,0.8)",
                          cargoMax: 400,
                        },
                        {
                          label:  "Kingston → Americas",
                          key:    "departing",
                          accent: "rgba(80,160,200,0.8)",
                          cargoMax: 80,
                        },
                      ].map(({ label, key, accent, cargoMax }) => {
                        const s = kingstonStats[key];
                        return (
                          <div key={key} style={{
                            marginBottom: 14, padding: 14, borderRadius: 8,
                            background: "rgba(255,255,255,0.04)",
                            borderLeft: `3px solid ${accent}`,
                          }}>
                            <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 6 }}>{label}</div>
                            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginBottom: 8 }}>
                              {s.count.toLocaleString()} voyages with complete records
                            </div>
                            <StatBar
                              label="Avg. persons embarked"
                              value={(s.avg_cargo / cargoMax) * 100}
                              displayValue={s.avg_cargo.toLocaleString()}
                              color={accent}
                              highlight={false}
                            />
                            <StatBar
                              label="Avg. mortality"
                              value={s.avg_mortality * 2.5}
                              displayValue={`${s.avg_mortality}%`}
                              color={accent}
                              highlight={s.avg_mortality > 15}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )
            }

            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", marginTop: 12, textAlign: "center" }}>
              Data: Slave Voyages database
            </div>
          </div>
        </div>
      )}

      <DeckGL
        initialViewState={INITIAL_VIEW}
        controller
        layers={layers}
        onClick={handleMapClick}
        getCursor={({ isHovering }) => isHovering ? "pointer" : "grab"}
        style={{ position: "absolute", inset: 0, height: "100%", width: "100%", overflow: "hidden" }}
        useDevicePixels={true}
      >
        <Map mapStyle={MAP_STYLE} />
      </DeckGL>
    </div>
  );
}